import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  History,
  Filter,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  X,
  Clock,
  Trash2,
  Edit3,
  Plus,
  GitMerge,
  Flag,
  ExternalLink,
  User as UserIcon,
} from "lucide-react";

const ACTION_FAMILIES = [
  { value: "all", label: "All actions" },
  { value: "clock:", label: "Clock actions" },
  { value: "event:", label: "Events (goals, cards, subs)" },
  { value: "result:", label: "Results" },
  { value: "match:patch", label: "Match edits" },
  { value: "match:delete", label: "Match deletions" },
  { value: "merge:", label: "Duplicate merges" },
];

const actionIcon = (action = "") => {
  if (action.startsWith("clock:")) return <Clock className="w-4 h-4 text-orange-500" />;
  if (action.startsWith("event:add")) return <Plus className="w-4 h-4 text-green-600" />;
  if (action.startsWith("event:delete")) return <Trash2 className="w-4 h-4 text-red-500" />;
  if (action.startsWith("result:")) return <Flag className="w-4 h-4 text-blue-600" />;
  if (action === "match:delete") return <Trash2 className="w-4 h-4 text-red-500" />;
  if (action.startsWith("match:")) return <Edit3 className="w-4 h-4 text-gray-700" />;
  if (action.startsWith("merge:")) return <GitMerge className="w-4 h-4 text-purple-600" />;
  return <History className="w-4 h-4 text-gray-400" />;
};

const prettyAction = (action = "") =>
  action.replace("clock:", "Clock → ").replace("event:add:", "Added ").replace("event:delete:", "Removed ")
        .replace("result:update", "Updated result").replace("match:patch", "Edited match")
        .replace("match:delete", "Deleted match").replace("merge:deleted_duplicate", "Merged duplicate")
        .replace(/_/g, " ");

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return iso; }
};

const AdminAuditLog = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null); // paginated envelope
  const [users, setUsers] = useState([]);
  // Filters
  const [matchId, setMatchId] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [actionFamily, setActionFamily] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);

  const loadUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/audit-log/distinct-users`, { withCredentials: true });
      setUsers(data.users || []);
    } catch { /* non-fatal */ }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (matchId.trim()) params.match_id = matchId.trim();
      if (userEmail) params.user_email = userEmail;
      if (actionFamily && actionFamily !== "all") params.action = actionFamily;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await axios.get(`${API}/admin/audit-log`, { params, withCredentials: true });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, matchId, userEmail, actionFamily, dateFrom, dateTo]);

  useEffect(() => { loadUsers(); }, []);
  useEffect(() => { load(); }, [load]);

  const clearFilters = () => {
    setMatchId(""); setUserEmail(""); setActionFamily("all"); setDateFrom(""); setDateTo(""); setPage(1);
  };

  const filtersActive = matchId || userEmail || (actionFamily !== "all") || dateFrom || dateTo;

  const renderDiff = (diff) => {
    if (!diff || !Object.keys(diff).length) return null;
    return (
      <div className="mt-2 space-y-0.5 text-[11px] font-mono bg-gray-50 border border-gray-200 p-2">
        {Object.entries(diff).map(([k, v]) => (
          <div key={k} className="flex flex-wrap gap-x-2">
            <span className="text-gray-500 min-w-[9rem]">{k}:</span>
            <span className="text-red-600 line-through">{String(v.before ?? "—")}</span>
            <span className="text-gray-400">→</span>
            <span className="text-green-700 font-semibold">{String(v.after ?? "—")}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <AdminLayout title="Audit Log">
      <div data-testid="admin-audit-log">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
          <p className="text-gray-500">Every admin-driven change to matches — who, what, when</p>
          <div className="flex gap-2">
            {filtersActive && (
              <Button variant="outline" onClick={clearFilters} className="rounded-none" data-testid="btn-clear-filters">
                <X className="w-4 h-4 mr-2" /> Clear filters
              </Button>
            )}
            <Button variant="outline" onClick={load} disabled={loading} className="rounded-none" data-testid="btn-refresh-audit">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 p-4 mb-6" data-testid="audit-filters">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Filters</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <Label className="text-[11px] uppercase text-gray-500">Match ID</Label>
              <Input
                placeholder="match_abc123…"
                value={matchId}
                onChange={(e) => { setPage(1); setMatchId(e.target.value); }}
                className="rounded-none mt-1 text-xs font-mono"
                data-testid="filter-match-id"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase text-gray-500">User</Label>
              <Select value={userEmail || "__any__"} onValueChange={(v) => { setPage(1); setUserEmail(v === "__any__" ? "" : v); }}>
                <SelectTrigger className="rounded-none mt-1 text-xs" data-testid="filter-user">
                  <SelectValue placeholder="Any user" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any user</SelectItem>
                  {users.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase text-gray-500">Action</Label>
              <Select value={actionFamily} onValueChange={(v) => { setPage(1); setActionFamily(v); }}>
                <SelectTrigger className="rounded-none mt-1 text-xs" data-testid="filter-action">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACTION_FAMILIES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase text-gray-500">From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => { setPage(1); setDateFrom(e.target.value); }}
                className="rounded-none mt-1 text-xs"
                data-testid="filter-date-from"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase text-gray-500">To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => { setPage(1); setDateTo(e.target.value); }}
                className="rounded-none mt-1 text-xs"
                data-testid="filter-date-to"
              />
            </div>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="space-y-2">
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
          </div>
        ) : data?.items?.length === 0 ? (
          <div className="bg-white border border-gray-200 p-16 text-center" data-testid="no-audit-entries">
            <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="font-heading text-2xl font-black uppercase text-gray-700">No audit entries</h3>
            <p className="text-gray-500 mt-2">
              {filtersActive ? "No entries match the current filters." : "Admin match actions will appear here as they happen."}
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs text-gray-500 mb-3">
              Showing {data.items.length} of <strong>{data.total}</strong> entries · Page {data.page} of {data.total_pages}
            </p>
            <div className="bg-white border border-gray-200 divide-y divide-gray-200" data-testid="audit-entries">
              {data.items.map((e) => (
                <div key={e.audit_id} className="p-4 hover:bg-gray-50 transition-colors" data-testid={`audit-entry-${e.audit_id}`}>
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 shrink-0">{actionIcon(e.action)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2">
                        <span className="font-semibold text-sm" data-testid="audit-action">{prettyAction(e.action)}</span>
                        <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100 text-[10px] font-mono">{e.action}</Badge>
                        {e.user_role === "admin" && (
                          <Badge className="bg-[#009E60] text-white hover:bg-[#009E60] text-[10px]">ADMIN</Badge>
                        )}
                        {e.user_role === "team_manager" && (
                          <Badge className="bg-[#FFC72C] text-black hover:bg-[#FFC72C] text-[10px]">MANAGER</Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-600">
                        <span className="inline-flex items-center gap-1">
                          <UserIcon className="w-3 h-3" />
                          <span data-testid="audit-user">{e.user_email || "—"}</span>
                        </span>
                        <span>·</span>
                        <span data-testid="audit-time">{fmtDateTime(e.created_at)}</span>
                        {e.match_meta ? (
                          <>
                            <span>·</span>
                            <Link
                              to={`/matches/${e.match_id}`}
                              className="inline-flex items-center gap-1 text-[#009E60] hover:underline"
                              data-testid="audit-match-link"
                            >
                              {e.match_meta.home_team?.short_name || e.match_meta.home_team?.name || "?"}
                              <span className="text-gray-400">vs</span>
                              {e.match_meta.away_team?.short_name || e.match_meta.away_team?.name || "?"}
                              <ExternalLink className="w-3 h-3" />
                            </Link>
                            {e.match_meta.competition && (
                              <span className="text-gray-500">· {e.match_meta.competition.name}</span>
                            )}
                            {e.match_meta.match_date && (
                              <span className="text-gray-500">· {e.match_meta.match_date}</span>
                            )}
                          </>
                        ) : (
                          <>
                            <span>·</span>
                            <span className="text-gray-400 italic">match {e.match_id?.replace("match_", "")} (deleted)</span>
                          </>
                        )}
                      </div>
                      {renderDiff(e.diff)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {data.total_pages > 1 && (
              <div className="flex items-center justify-between mt-4" data-testid="audit-pagination">
                <Button
                  variant="outline"
                  disabled={!data.has_previous}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-none"
                  data-testid="btn-page-prev"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                </Button>
                <span className="text-sm text-gray-600">Page {data.page} of {data.total_pages}</span>
                <Button
                  variant="outline"
                  disabled={!data.has_next}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded-none"
                  data-testid="btn-page-next"
                >
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminAuditLog;
